<div class="crm-page">
  <div class="crm-container">
    <!-- TOP BAR -->
    <header class="crm-top">
      <div class="brand">
        <div class="hero-logo">
          <img src="../../assets/images/Logo.svg" alt="Nishwik Logo" class="img-fluid" />
        </div>
        <span class="module-chip"><i class="fas fa-handshake"></i> CRM</span>
      </div>
      <div class="top-link">
        Already have an account?
        <a routerLink="/login">Sign in</a>
      </div>
    </header>

    <main class="crm-main">
      <!-- SHOWCASE -->
      <section class="showcase">
        <h1 class="headline">Start selling from one shared workspace.</h1>
        <p class="lede">
          Set up takes about two minutes. Once you are in, you can add leads,
          assign owners and track every deal to the close.
        </p>

        <ol class="steps">
          <li class="step">
            <span class="step-dot"><i class="fas fa-user-plus"></i></span>
            <div class="step-text">
              <strong>Create your account</strong>
              <span>Your name, work email and a secure password.</span>
            </div>
          </li>
          <li class="step">
            <span class="step-dot"><i class="fas fa-mobile-alt"></i></span>
            <div class="step-text">
              <strong>Verify your mobile number</strong>
              <span>A 6-digit code confirms it is really you.</span>
            </div>
          </li>
          <li class="step">
            <span class="step-dot done"><i class="fas fa-bullseye"></i></span>
            <div class="step-text">
              <strong>Add your first leads</strong>
              <span>Enter them one by one or bring them in from a spreadsheet.</span>
            </div>
          </li>
        </ol>

        <div class="proof">
          <div class="proof-item">
            <i class="fas fa-shield-alt"></i>
            <span>Your data stays encrypted in transit and at rest.</span>
          </div>
          <div class="proof-item">
            <i class="fas fa-users"></i>
            <span>Invite your team later. Everyone sees the same pipeline.</span>
          </div>
        </div>
      </section>

      <!-- FORM -->
      <section class="auth-side">
        <div class="auth-card signup-card">
          <div class="card-inner">
            <h2 class="card-title">Create your account</h2>
            <p class="card-subtitle">Fields marked <span class="req">*</span> are required.</p>

            <form [formGroup]="signupForm" (ngSubmit)="onSubmit()">
              <div class="form-row">
                <div class="input-group">
                  <label for="s-first">First name <span class="req">*</span></label>
                  <div class="input-wrap">
                    <i class="fas fa-user input-icon"></i>
                    <input id="s-first" type="text" formControlName="firstName" placeholder="Aarav" autocomplete="given-name" />
                  </div>
                  <small class="field-error" *ngIf="signupForm.get('firstName')?.touched && signupForm.get('firstName')?.hasError('required')">
                    Enter your first name.
                  </small>
                </div>
                <div class="input-group">
                  <label for="s-last">Last name <span class="req">*</span></label>
                  <div class="input-wrap">
                    <i class="fas fa-user input-icon"></i>
                    <input id="s-last" type="text" formControlName="lastName" placeholder="Sharma" autocomplete="family-name" />
                  </div>
                  <small class="field-error" *ngIf="signupForm.get('lastName')?.touched && signupForm.get('lastName')?.hasError('required')">
                    Enter your last name.
                  </small>
                </div>
              </div>

              <div class="input-group">
                <label for="s-email">Work email <span class="req">*</span></label>
                <div class="input-wrap">
                  <i class="fas fa-envelope input-icon"></i>
                  <input id="s-email" type="email" formControlName="email" placeholder="you@company.com" autocomplete="email" />
                </div>
                <small class="field-error" *ngIf="signupForm.get('email')?.touched && signupForm.get('email')?.hasError('required')">Enter your email address.</small>
                <small class="field-error" *ngIf="signupForm.get('email')?.touched && signupForm.get('email')?.hasError('email')">Enter a valid email address.</small>
              </div>

              <div class="input-group">
                <label for="s-mobile">Mobile number <span class="req">*</span></label>
                <div class="mobile-row">
                  <div class="input-wrap">
                    <i class="fas fa-phone-alt input-icon"></i>
                    <input id="s-mobile" type="tel" formControlName="mobileNumber" placeholder="10-digit mobile number" maxlength="10" inputmode="numeric" autocomplete="tel-national" />
                  </div>
                  <button
                    type="button"
                    class="btn-ghost"
                    (click)="sendOtp()"
                    [disabled]="signupForm.get('mobileNumber')?.invalid || (otpSent && otpTimer > 0)"
                  >
                    <span *ngIf="!otpSent">Send OTP</span>
                    <span *ngIf="otpSent && otpTimer > 0">{{ otpTimer }}s</span>
                    <span *ngIf="otpSent && otpTimer === 0">Resend</span>
                  </button>
                </div>
                <small class="field-error" *ngIf="signupForm.get('mobileNumber')?.touched && signupForm.get('mobileNumber')?.invalid">
                  Enter a valid 10-digit mobile number.
                </small>
              </div>

              <div class="input-group otp-group" *ngIf="otpSent">
                <label for="s-otp">Verification code <span class="req">*</span></label>
                <div class="input-wrap">
                  <i class="fas fa-shield-alt input-icon"></i>
                  <input id="s-otp" type="text" formControlName="otp" placeholder="6-digit code" maxlength="6" inputmode="numeric" autocomplete="one-time-code" />
                </div>
                <small class="field-error" *ngIf="signupForm.get('otp')?.touched && signupForm.get('otp')?.invalid">
                  Enter the 6-digit code.
                </small>
              </div>

              <div class="input-group">
                <label for="s-user">Username <span class="req">*</span></label>
                <div class="input-wrap">
                  <i class="fas fa-at input-icon"></i>
                  <input id="s-user" type="text" formControlName="username" placeholder="Choose a username" autocomplete="username" />
                </div>
                <small class="field-error" *ngIf="signupForm.get('username')?.touched && signupForm.get('username')?.hasError('required')">Choose a username.</small>
              </div>

              <div class="form-row">
                <div class="input-group">
                  <label for="s-pass">Password <span class="req">*</span></label>
                  <div class="input-wrap">
                    <i class="fas fa-lock input-icon"></i>
                    <input id="s-pass" type="password" formControlName="password" placeholder="Min. 6 characters" autocomplete="new-password" />
                  </div>
                  <small class="field-error" *ngIf="signupForm.get('password')?.touched && signupForm.get('password')?.invalid">
                    Use at least 6 characters.
                  </small>
                </div>
                <div class="input-group">
                  <label for="s-confirm">Confirm password <span class="req">*</span></label>
                  <div class="input-wrap">
                    <i class="fas fa-check-circle input-icon"></i>
                    <input id="s-confirm" type="password" formControlName="confirmPassword" placeholder="Re-enter password" autocomplete="new-password" />
                  </div>
                  <small class="field-error" *ngIf="signupForm.hasError('mismatch') && signupForm.get('confirmPassword')?.touched">
                    Passwords do not match.
                  </small>
                </div>
              </div>

              <button type="submit" class="btn-submit" [disabled]="signupForm.invalid">
                <span>Create account</span>
              </button>

              <p class="terms-text">
                By creating an account you agree to our
                <a routerLink="/policy/terms">Terms &amp; Conditions</a> and
                <a routerLink="/policy/privacy">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </div>
      </section>
    </main>

    <footer class="bottom-bar">
      <p>&copy; 2026 Nishwik CRM. All rights reserved.</p>
      <div class="bottom-links">
        <a routerLink="/policy/privacy">Privacy Policy</a>
        <a routerLink="/policy/terms">Terms &amp; Conditions</a>
        <a routerLink="/policy/contact">Contact us</a>
      </div>
    </footer>
  </div>
</div>
